import React, { useState, useEffect, useRef } from 'react';
import { WebsiteDesignProps } from './types';
import { 
  Building2, Book, Globe, Award, ChevronDown, 
  MapPin, Phone, Mail, ArrowRight, ArrowUpRight, Instagram, Linkedin, Facebook,
  Youtube, Menu, X, Star, Users, Trophy, School, Clock, 
  CheckCircle2, Info, GraduationCap, ChevronLeft, ChevronRight,
  Download, Send, Camera, Plus, Layout, Sparkles, Heart, Quote, BookOpen,
  MessageCircle, Target, HelpCircle, Image as ImageIcon, Building2 as BuildingIcon
} from 'lucide-react';
import { Button } from "@/components/ui/button";

// =============================================
// DESIGN 3 — CLASSIC IVY
// Backbone: 100% identical to Design0_Features
// UI: Original ClassicIvy formal visual design preserved
// =============================================

const ClassicIvy: React.FC<WebsiteDesignProps> = ({ previewData, isEditMode, onUpdateField }) => {

  // ── BACKBONE STATE (from Design0) ──────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('All');
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedView, setExpandedView] = useState<'staff' | 'board' | 'gallery' | 'facilities' | 'anthems' | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  // ── CLASSIC IVY ORIGINAL STATE ───────────────────────────────────────────
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // ── BACKBONE EFFECTS ──────────────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const safeUrl = (file: any) => {
    if (!file) return undefined;
    if (typeof file === 'string') return file;
    try { return URL.createObjectURL(file); } catch (e) { return undefined; }
  };

  // ── BACKBONE DATA OBJECT — EXACT COPY FROM DESIGN0 ────────────────────────
  const data = {
    name: previewData?.name || "St. Augustine's National Academy",
    motto: previewData?.motto || "In Veritate et Virtute (In Truth and Virtue)",
    logo: previewData?.logo ? safeUrl(previewData.logo) : "https://images.unsplash.com/photo-1599305090598-fe179d501227?q=80&w=256&auto=format&fit=crop",
    headerPhotos: (previewData?.headerPhotos && previewData.headerPhotos.filter((p: any) => p !== null).length > 0) ? previewData.headerPhotos : [
      "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=1200&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1541339907198-e08756ebafe3?q=80&w=1200&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=1200&auto=format&fit=crop"
    ],
    stats: {
      students: previewData?.totalStudents || "1,450",
      staff: previewData?.totalStaff || "92",
      founded: previewData?.yearFounded || "1974",
      alumni: previewData?.totalAlumni || "8,000+"
    },
    principal: {
      name: previewData?.principalName || "Dr. Richard M. Mutua",
      message: previewData?.principalMessage || "Welcome to St. Augustine's, a leading institution where we harmonize academic discipline with character development to mold the leaders of tomorrow.",
      photo: previewData?.principalPhoto ? safeUrl(previewData.principalPhoto) : "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=400&auto=format&fit=crop"
    },
    mission: previewData?.mission || "To provide a holistic and inclusive learning environment that fosters academic brilliance, integrity, and global citizenship.",
    vision: previewData?.vision || "To be the premier national center of excellence, recognized globally for academic innovation and transformative leadership.",
    about: previewData?.about || "A distinguished national institution dedicated to the pursuit of excellence in all spheres of life.",
    history: previewData?.historyText || "Established in 1974 as a community initiative, our school was later elevated to National status in 2011, continuing a heritage of producing top-tier professionals and leaders for the continent.",
    aboutPhoto: previewData?.aboutPhoto ? safeUrl(previewData.aboutPhoto) : "https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=2070",
    missionPhoto: previewData?.missionPhoto ? safeUrl(previewData.missionPhoto) : "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=2070",
    visionPhoto: previewData?.visionPhoto ? safeUrl(previewData.visionPhoto) : "https://images.unsplash.com/photo-1544928147-79a2dbc1f389?q=80&w=2070",
    whyChooseUsPhoto: previewData?.whyChooseUsPhoto ? safeUrl(previewData.whyChooseUsPhoto) : "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?q=80&w=2070",
    nationalAnthem: previewData?.nationalAnthem || "O God of all creation\nBless this our land and nation\nJustice be our shield and defender\nMay we dwell in unity\nPeace and liberty\nPlenty be found within our borders.\n\nLet one and all arise\nWith hearts both strong and true\nService be our earnest endeavour\nAnd our homeland of Kenya\nHeritage of splendour\nFirm may we stand to defend.\n\nLet all with one accord\nIn common bond united\nBuild this our nation together\nAnd the glory of Kenya\nThe fruit of our labour\nFill every heart with thanksgiving.",
    schoolAnthem: previewData?.schoolAnthem || "Hail to our alma mater, standing tall and true,\nGuiding lights of knowledge, red and brilliant blue.\nIn these halls of wisdom, we learn to lead and serve,\nBuilding strong foundations, with unyielding nerve.\n\nThrough the trials and triumphs, together we will rise,\nReaching for the future, beneath the open skies.\nHonor, truth, and courage, forever be our guide,\nIn our school's great legacy, we take eternal pride!",
    whyUs: previewData?.whyChooseUs || "We combine a tradition of excellence with state-of-the-art facilities and a passionate faculty dedicated to cross-disciplinary learning.",
    coreValues: previewData?.coreValues || ["Integrity", "Excellence", "Innovation", "Inclusivity", "Service"],
    quote: {
      text: previewData?.quote || "The beautiful thing about learning is that no one can take it away from you.",
      author: previewData?.quoteAuthor || "B.B. King"
    },
    curriculum: {
      description: previewData?.curriculumDesc || "Our comprehensive curriculum follows the National Competency Based framework, enhanced with international STEM integration for a global perspective.",
      compulsory: previewData?.compulsorySubjects || ["English", "Mathematics", "Kiswahili", "Chemistry", "Biology", "Physics"],
      optional: previewData?.optionalSubjects || ["Computer Studies", "French", "Business Studies", "Agriculture", "Music", "Aviation"],
      photo: previewData?.curriculumPhoto ? safeUrl(previewData.curriculumPhoto) : "https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=2070"
    },
    activities: (previewData?.activities && previewData.activities.length > 0) ? previewData.activities : [
      { id: "1", title: "Robotics Club", description: "Design and program modern robots with Python.", photo: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=400&auto=format&fit=crop" },
      { id: "2", title: "Drama Society", description: "Annual theatrical productions and national competitions.", photo: "https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?q=80&w=400&auto=format&fit=crop" },
      { id: "3", title: "Basketball Team", description: "Our varsity team 'The Eagles' are the regional champions.", photo: "https://images.unsplash.com/photo-1546519638-68e109498ffc?q=80&w=400&auto=format&fit=crop" }
    ],
    facilities: (previewData?.facilities && previewData.facilities.length > 0) ? previewData.facilities : [
      { id: "1", categoryId: "Technology", title: "Modern Science Lab", description: "Fully equipped biology and chemistry laboratories.", photoFile: "https://images.unsplash.com/photo-1562774053-701939374585?q=80&w=400&auto=format&fit=crop" },
      { id: "2", categoryId: "Academic", title: "Digital Library", description: "Access to over 10,000 e-books and research journals.", photoFile: "https://images.unsplash.com/photo-1521587760476-6c12a7b041da?q=80&w=400&auto=format&fit=crop" },
      { id: "3", categoryId: "Sports", title: "Indoor Sports Arena", description: "Olympic-sized pool and multi-purpose gymnasium.", photoFile: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=400&auto=format&fit=crop" },
      { id: "4", categoryId: "Arts", title: "Creative Arts Center", description: "Soundproof music studios and fine arts workshops.", photoFile: "https://images.unsplash.com/photo-1460518451285-8f16c4f0340b?q=80&w=400&auto=format&fit=crop" },
      { id: "5", categoryId: "Medical", title: "Student Health Clinic", description: "24/7 medical services with trained resident nurses.", photoFile: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?q=80&w=400&auto=format&fit=crop" },
      { id: "6", categoryId: "Residential", title: "Premium Boarding Houses", description: "Comfortable, modern living spaces with high-speed internet.", photoFile: "https://images.unsplash.com/photo-1555854877-bab0e564b8d5?q=80&w=400&auto=format&fit=crop" }
    ],
    staff: (previewData?.staffMembers && previewData.staffMembers.length > 0) ? previewData.staffMembers : [
      { id: "1", title: "Mr.", name: "Peter Otieno", bio: "HOD Sciences", photoFile: "https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=256&auto=format&fit=crop" },
      { id: "2", title: "Ms.", name: "Sarah Wanjiku", bio: "HOD Humanities", photoFile: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=256&auto=format&fit=crop" },
      { id: "3", title: "Dr.", name: "David Kimani", bio: "HOD Languages", photoFile: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=256&auto=format&fit=crop" },
      { id: "4", title: "Mrs.", name: "Mary Atieno", bio: "HOD Mathematics", photoFile: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=256&auto=format&fit=crop" },
      { id: "5", title: "Mr.", name: "John Kamau", bio: "Sports Director", photoFile: "https://images.unsplash.com/photo-1566492031773-4f4e44671857?q=80&w=256&auto=format&fit=crop" },
      { id: "6", title: "Ms.", name: "Grace Njeri", bio: "Counselor", photoFile: "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=256&auto=format&fit=crop" }
    ],
    board: (previewData?.boardMembers && previewData.boardMembers.length > 0) ? previewData.boardMembers : [
      { id: "1", title: "Rev.", name: "John Mark", role: "Chairman BOM", photoFile: "https://images.unsplash.com/photo-1463453091185-61582044d556?q=80&w=256&auto=format&fit=crop" },
      { id: "2", title: "Dr.", name: "Alice Mwangi", role: "Treasurer", photoFile: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=256&auto=format&fit=crop" },
      { id: "3", title: "Hon.", name: "Samuel Korir", role: "Community Rep", photoFile: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?q=80&w=256&auto=format&fit=crop" },
      { id: "4", title: "Eng.", name: "Francis Njuguna", role: "Infrastructure", photoFile: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=256&auto=format&fit=crop" },
      { id: "5", title: "Prof.", name: "Jane Kemuntu", role: "Advisor", photoFile: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=256&auto=format&fit=crop" },
      { id: "6", title: "Lady Justice", name: "Ruth Kerubo", role: "Legal", photoFile: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=256&auto=format&fit=crop" }
    ],
    gallery: (previewData?.galleryItems && previewData.galleryItems.length > 0) ? previewData.galleryItems : [
      { id: "1", categoryId: "Campus", name: "Main Wing", description: "Our historic main administration building.", photoFile: "https://images.unsplash.com/photo-1525921429624-479b6a29d840?q=80&w=600&auto=format&fit=crop" },
      { id: "2", categoryId: "Events", name: "Sports Day", description: "Annual regional championships.", photoFile: "https://images.unsplash.com/photo-1511578314322-379afb476865?q=80&w=600&auto=format&fit=crop" },
      { id: "3", categoryId: "Infrastructure", name: "Library", description: "State-of-the-art research center.", photoFile: "https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=600&auto=format&fit=crop" },
      { id: "4", categoryId: "Students", name: "Tech Club", description: "Students building a robotics prototype.", photoFile: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=600&auto=format&fit=crop" },
      { id: "5", categoryId: "Trips", name: "Mountain Hike", description: "Biology class exploring Mount Kenya flora.", photoFile: "https://images.unsplash.com/photo-1503220317375-aaad61436b1b?q=80&w=600&auto=format&fit=crop" }
    ],
    contact: {
      phones: previewData?.phones || ["+254 712 345 678", "+254 789 012 345"],
      email: previewData?.email || "info@staugustines.ac.ke",
      whatsapp: previewData?.whatsapp || "254712345678",
      facebook: previewData?.fbLink || "",
      twitter: previewData?.twitterLink || "",
      instagram: previewData?.instaLink || "",
      address: previewData?.address || "Main Campus, Kisumu-Kakamega Road, Kisumu",
      mapsLink: previewData?.mapsLink || "https://www.google.com/maps/embed?pb=..."
    },
    admissions: {
      process: previewData?.admissionProcess || "Our intake happens annually in January. Prospective students must submit their applications through our online portal or in-person at the Registrar's Office.",
      requirements: previewData?.admissionRequirements || ["Certified Birth Certificate", "KCPE Result Slip", "Leaving Certificate", "Medical Examination Report"],
      formFile: previewData?.admissionFormFile || null
    },
    payments: (previewData?.paymentMethods && previewData.paymentMethods.length > 0) ? previewData.paymentMethods : [
      { id: "1", method: "M-Pesa Paybill", details: "Business No: 123456 | Account: Student Name" },
      { id: "2", method: "KCB Bank", details: "Acc Name: St. Augustine's | Acc No: 0987654321" }
    ],
    feePolicy: previewData?.feeBreakdown || "School fees are due on or before the first day of each term. Payment plans are available upon request through the Finance Office."
  };

  // ── BACKBONE EFFECTS CONTINUED ────────────────────────────────────────────
  useEffect(() => {
    if (data.headerPhotos.length <= 1) return;
    const interval = setInterval(() => setCurrentSlide(prev => (prev + 1) % data.headerPhotos.length), 5000);
    return () => clearInterval(interval);
  }, [data.headerPhotos.length]);

  const filteredGallery = data.gallery.filter((item: any) => activeTab === 'All' || (item.categoryId || item.category) === activeTab);

  // ── CLASSIC IVY THEME EFFECT ─────────────────────────────────────────────
  useEffect(() => {
    const doc = containerRef.current?.ownerDocument || document;
    const root = doc.documentElement;
    if (previewData?.theme) {
      const { colors, fonts } = previewData.theme;
      root.style.setProperty('--color-primary', colors.primary || '#800000');
      root.style.setProperty('--color-secondary', colors.secondary || '#1e3a8a');
      root.style.setProperty('--color-accent', colors.accent || '#D4AF37');
      root.style.setProperty('--color-background', colors.background || '#fdfdfd');
      root.style.setProperty('--color-text', colors.text || '#1a1a1a');
      root.style.setProperty('--font-heading', `'${fonts.heading || 'Playfair Display'}', serif`);
      root.style.setProperty('--font-body', `'${fonts.body || 'Crimson Pro'}', serif`);
      const radiusMap: any = { none: '0px', small: '2px', medium: '4px', large: '8px', full: '9999px' };
      root.style.setProperty('--radius', radiusMap[previewData.theme.borderRadius || 'none']);
    }
    const fontLink = doc.createElement('link');
    fontLink.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Crimson+Pro:wght@300;400;600&display=swap";
    fontLink.rel = 'stylesheet';
    doc.head.appendChild(fontLink);
  }, [previewData?.theme]);

  const handleUpdate = (path: string, val: any) => onUpdateField && onUpdateField(path, val);

  const nav = [
    { label: 'About', href: '#about' },
    { label: 'Academics', href: '#academics' },
    { label: 'Faculty', href: '#staff' },
    { label: 'Gallery', href: '#gallery' },
    { label: 'Admissions', href: '#admissions' },
    { label: 'Contact', href: '#contact' }
  ];

  return (
    <div ref={containerRef} className="classic-ivy text-[var(--color-text)] bg-[var(--color-background)] min-h-screen font-[var(--font-body)]">

      {/* ── TOP BAR (ClassicIvy design) ── */}
      <div className="bg-[#1a1a1a] text-gray-400 py-3 px-8 text-[10px] tracking-[0.2em] uppercase font-bold justify-between items-center border-b border-white/10 hidden md:flex">
        <div className="flex gap-8">
          <span className="flex items-center gap-2 hover:text-white transition-colors cursor-pointer"><Clock size={12} /> Admissions Open {new Date().getFullYear()}</span>
          <span className="flex items-center gap-2 hover:text-white transition-colors cursor-pointer"><Info size={12} /> Alumni Association</span>
        </div>
        <div className="flex gap-8">
          {data.contact.phones[0] && <span className="flex items-center gap-2"><Phone size={12} /> {data.contact.phones[0]}</span>}
          <span className="flex items-center gap-2"><MapPin size={12} /> Our Campus</span>
        </div>
      </div>

      {/* ── ELEGANT HEADER (ClassicIvy design) ── */}
      <header className="sticky top-0 z-[60] bg-white border-b border-gray-200 py-5 px-8 shadow-sm">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-5">
            <div className="w-12 h-12 bg-[var(--color-primary)]/5 p-1.5 flex items-center justify-center overflow-hidden">
              {data.logo ? <img src={safeUrl(data.logo)} alt="Logo" className="w-full h-full object-contain" /> : <School size={28} className="text-[var(--color-primary)]" />}
            </div>
            <h1 className="text-xl md:text-2xl font-bold tracking-tight text-[var(--color-primary)]" style={{ fontFamily: 'var(--font-heading)' }}>{data.name}</h1>
          </div>

          <nav className="hidden lg:flex gap-10 text-[11px] font-bold uppercase tracking-[0.2em]">
            {nav.map((n, i) => (
              <a key={i} href={n.href} className="text-gray-600 hover:text-[var(--color-primary)] transition-all relative group">
                {n.label}
                <span className="absolute -bottom-2 left-1/2 w-0 h-[2px] bg-[var(--color-primary)] transition-all group-hover:w-full group-hover:left-0" />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-5">
            <div className="relative">
              <Button onClick={() => setShowLoginDropdown(!showLoginDropdown)} variant="outline" className="border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white rounded-none px-6 font-bold uppercase text-[10px] tracking-widest transition-all">
                Portal <ChevronDown size={13} className={`ml-2 transition-transform ${showLoginDropdown ? 'rotate-180' : ''}`} />
              </Button>
              {showLoginDropdown && (
                <div className="absolute top-full right-0 mt-3 w-52 bg-white border border-gray-200 shadow-2xl p-1 z-[70]">
                  <a href="#" className="flex items-center gap-3 p-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 hover:bg-[var(--color-primary)] hover:text-white transition-all">
                    <Heart size={13} /> Parent Portal
                  </a>
                  <a href="#" className="flex items-center gap-3 p-3 text-[10px] font-bold uppercase tracking-widest text-gray-700 hover:bg-[var(--color-primary)] hover:text-white transition-all">
                    <Star size={13} /> Student Portal
                  </a>
                </div>
              )}
            </div>
            <button className="lg:hidden text-[var(--color-primary)]" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>

      {/* ── HERO — Full height, grayscale bone (ClassicIvy design, backbone slideshow) ── */}
      <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          {data.headerPhotos.map((p: any, i: number) => (
            <img key={i} src={safeUrl(p)} alt={`Hero ${i}`} className={`absolute inset-0 w-full h-full object-cover grayscale-[0.2] transition-all duration-[2000ms] ${i === currentSlide ? 'opacity-100' : 'opacity-0'}`} />
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/60" />
        </div>
        <div className="relative z-10 text-center text-white max-w-5xl px-6">
          <div className="w-24 h-[3px] bg-[var(--color-accent)] mx-auto mb-10 shadow-lg" />
          <div className="text-sm font-bold tracking-[0.4em] uppercase mb-8 opacity-80">Est. {data.stats.founded} • A Legacy of Excellence</div>
          <h1 className="text-5xl md:text-7xl font-medium mb-8 leading-[1.1] drop-shadow-2xl" style={{ fontFamily: 'var(--font-heading)' }}>
            A Tradition of Excellence <br />
            <i className="text-[var(--color-accent)] italic font-light">{data.name}</i>
          </h1>
          <p className="text-lg md:text-xl font-light opacity-90 mb-12 tracking-wide max-w-3xl mx-auto leading-relaxed italic" style={{ fontFamily: 'var(--font-body)' }}>
            "{data.motto}"
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <a href="#about" className="bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90 text-white h-12 px-10 font-bold uppercase tracking-[0.2em] text-[10px] shadow-2xl transition-all hover:scale-105 inline-flex items-center gap-2">
              Our Heritage <ArrowRight size={13} />
            </a>
            <a href="#admissions" className="bg-transparent border border-white text-white hover:bg-white hover:text-black h-12 px-10 font-bold uppercase tracking-[0.2em] text-[10px] transition-all backdrop-blur-sm inline-flex items-center gap-2">
              Admissions
            </a>
          </div>
        </div>
        {/* Slide indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3">
          {data.headerPhotos.map((_: any, i: number) => (
            <button key={i} onClick={() => setCurrentSlide(i)} className={`h-[2px] transition-all bg-white ${i === currentSlide ? 'w-8 opacity-100' : 'w-4 opacity-40'}`} aria-label={`Go to slide ${i + 1}`} title={`Go to slide ${i + 1}`} />
          ))}
        </div>
      </section>

      {/* ── THREE PILLARS (ClassicIvy design, backbone data) ── */}
      <section className="grid grid-cols-1 md:grid-cols-3 bg-[#1a1a1a] text-white">
        {[
          { icon: Award, title: "Academic Rigor", desc: data.mission },
          { icon: Globe, title: "Global Perspective", desc: data.vision },
          { icon: Building2, title: "Community Values", desc: data.whyUs }
        ].map((item, i) => (
          <div key={i} className="p-14 md:p-20 border-b md:border-b-0 border-r border-white/5 hover:bg-[#222] transition-all duration-700 group cursor-default relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-0 bg-[var(--color-accent)] transition-all group-hover:h-full" />
            <div className="w-14 h-14 bg-[var(--color-primary)]/20 flex items-center justify-center text-[var(--color-accent)] mb-8 group-hover:scale-110 transition-transform">
              <item.icon size={28} />
            </div>
            <h3 className="text-2xl font-[var(--font-heading)] italic mb-5 text-[var(--color-accent)] group-hover:text-white transition-colors">{item.title}</h3>
            <p className="font-light text-gray-400 leading-relaxed mb-6 line-clamp-4" style={{ fontFamily: 'var(--font-body)' }}>{item.desc}</p>
          </div>
        ))}
      </section>

      {/* ── ABOUT / HISTORY (ClassicIvy design, backbone data) ── */}
      <section id="about" className="py-32 bg-white overflow-hidden">
        <div className="container mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
            <div className="relative group">
              <div className="absolute -inset-4 border-2 border-[var(--color-accent)] translate-x-8 translate-y-8 -z-10 group-hover:translate-x-4 group-hover:translate-y-4 transition-transform duration-700" />
              <img src={data.aboutPhoto} className="w-full shadow-2xl grayscale-[0.3] group-hover:grayscale-0 transition-all duration-1000" alt="School History" />
              <div className="absolute -bottom-10 -left-10 bg-[var(--color-primary)] text-white p-10 shadow-2xl hidden md:block">
                <h4 className="text-3xl font-[var(--font-heading)] mb-1 italic">Est. {data.stats.founded}</h4>
                <p className="text-[10px] font-bold uppercase tracking-widest opacity-60">Legacy of Leadership</p>
              </div>
            </div>
            <div className="lg:pl-10">
              <div className="w-16 h-[2px] bg-[var(--color-primary)] mb-8" />
              <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-gray-400 mb-6">Our Narrative</div>
              <h2 className="text-4xl md:text-6xl font-medium text-[var(--color-primary)] mb-10 leading-tight italic" style={{ fontFamily: 'var(--font-heading)' }}>School History.</h2>
              <p className="text-xl text-gray-600 leading-relaxed italic mb-10 font-light">{data.history}</p>

              {/* Stats in ClassicIvy style */}
              <div className="grid grid-cols-2 gap-8 border-t border-gray-100 pt-10 mb-10">
                {[
                  { value: data.stats.students, label: "Students Enrolled" },
                  { value: data.stats.staff, label: "Academic Staff" },
                  { value: data.stats.alumni, label: "Alumni Network" },
                  { value: data.stats.founded, label: "Year Founded" }
                ].map((s, i) => (
                  <div key={i}>
                    <div className="text-4xl font-[var(--font-heading)] italic text-[var(--color-accent)] mb-1">{s.value}</div>
                    <div className="text-[10px] font-bold uppercase tracking-widest opacity-50">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Vision & Mission */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-10">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-primary)] mb-3">Our Vision</div>
                  <p className="text-sm text-gray-500 leading-relaxed font-light italic">{data.vision}</p>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-primary)] mb-3">Our Mission</div>
                  <p className="text-sm text-gray-500 leading-relaxed font-light italic">{data.mission}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US & ANTHEMS (ClassicIvy design, backbone data) ── */}
      <section className="py-32 bg-[#f9f9f9] border-y border-gray-100">
        <div className="container mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-24 items-start">
            <div className="space-y-12">
              <div>
                <div className="w-16 h-[2px] bg-[var(--color-primary)] mb-8" />
                <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-gray-400 mb-6">Our Distinction</div>
                <h2 className="text-4xl md:text-6xl font-[var(--font-heading)] italic text-[var(--color-primary)] mb-8">Why Choose Us.</h2>
                <p className="text-xl text-gray-500 font-light italic leading-relaxed">{data.whyUs}</p>
              </div>
              {/* Core Values in ClassicIvy style */}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[var(--color-accent)] mb-6">Core Values</div>
                <div className="flex flex-wrap gap-3">
                  {data.coreValues.map((v: string, i: number) => (
                    <span key={i} className="border border-[var(--color-primary)]/20 text-[var(--color-primary)] px-5 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-[var(--color-primary)] hover:text-white transition-all cursor-default">{v}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* Anthems in ClassicIvy dark formal style */}
            <div className="bg-[#1a1a1a] p-12 space-y-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-10 opacity-5 rotate-12"><Quote size={180} /></div>
              <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-[var(--color-accent)] relative z-10">Institutional Anthems</div>
              <div className="grid sm:grid-cols-2 gap-6 relative z-10">
                <div
                  onClick={() => setExpandedView('anthems')}
                  className="p-8 bg-white/5 border border-white/10 cursor-pointer hover:border-[var(--color-accent)] transition-all group"
                >
                  <div className="flex justify-between items-center mb-4">
                    <p className="text-sm font-bold uppercase tracking-[0.3em] text-gray-300">National Anthem</p>
                    <ArrowUpRight size={16} className="text-[var(--color-accent)]" />
                  </div>
                  <p className="text-xs italic leading-relaxed text-gray-500 line-clamp-2">{data.nationalAnthem}</p>
                </div>
                <div
                  onClick={() => setExpandedView('anthems')}
                  className="p-8 bg-white/5 border border-white/10 cursor-pointer hover:border-[var(--color-accent)] transition-all group"
                >
                  <div className="flex justify-between items-center mb-4">
                    <p className="text-sm font-bold uppercase tracking-[0.3em] text-gray-300">School Anthem</p>
                    <ArrowUpRight size={16} className="text-[var(--color-accent)]" />
                  </div>
                  <p className="text-xs italic leading-relaxed text-gray-500 line-clamp-2">{data.schoolAnthem}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── ACADEMICS / CURRICULUM (ClassicIvy design, backbone data) ── */}
      <section id="academics" className="py-32 bg-[#fdfdfd]">
        <div className="container mx-auto px-8">
          <div className="text-center mb-24 max-w-3xl mx-auto">
            <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-[var(--color-primary)] mb-6">Educational Program</div>
            <h2 className="text-5xl md:text-7xl font-medium text-[var(--color-primary)] mb-8 italic" style={{ fontFamily: 'var(--font-heading)' }}>Academics.</h2>
            <div className="w-16 h-[2px] bg-[var(--color-accent)] mx-auto mb-10" />
            <p className="text-xl text-gray-500 font-light italic leading-relaxed">{data.curriculum.description}</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative group">
              <div className="absolute -inset-4 border-2 border-[var(--color-accent)]/30 translate-x-6 translate-y-6 -z-10 group-hover:translate-x-3 group-hover:translate-y-3 transition-transform duration-700" />
              <img src={data.curriculum.photo} className="w-full shadow-xl grayscale-[0.2] group-hover:grayscale-0 transition-all duration-1000 aspect-[4/3] object-cover" alt="Curriculum" />
            </div>
            <div className="space-y-8">
              <div className="border border-gray-100 bg-white shadow-sm p-10">
                <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[var(--color-primary)] mb-6 border-b border-gray-100 pb-4 flex items-center gap-3"><BookOpen size={16} /> Core Subjects</div>
                <div className="flex flex-wrap gap-2">
                  {data.curriculum.compulsory.map((s: string, i: number) => (
                    <span key={i} className="border border-[var(--color-primary)]/20 text-[var(--color-primary)] px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider">{s}</span>
                  ))}
                </div>
              </div>
              <div className="bg-[var(--color-primary)] p-10 shadow-sm">
                <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[var(--color-accent)] mb-6 border-b border-white/10 pb-4 flex items-center gap-3"><ArrowRight size={16} className="text-[var(--color-accent)]" /> Elective Subjects</div>
                <div className="flex flex-wrap gap-2">
                  {data.curriculum.optional.map((s: string, i: number) => (
                    <span key={i} className="border border-white/20 text-white px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider">{s}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CLUBS & ACTIVITIES (ClassicIvy design, backbone data) ── */}
      <section className="py-32 bg-white border-y border-gray-100">
        <div className="container mx-auto px-8">
          <div className="text-center mb-24">
            <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-[var(--color-primary)] mb-6">Holistic Development</div>
            <h2 className="text-5xl md:text-7xl font-medium text-[var(--color-primary)] italic" style={{ fontFamily: 'var(--font-heading)' }}>Co-Curricular Life.</h2>
            <div className="w-16 h-[2px] bg-[var(--color-accent)] mx-auto mt-8" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {data.activities.map((a: any, i: number) => (
              <div key={i} className="group border border-gray-100 bg-white hover:shadow-2xl transition-all duration-700 overflow-hidden cursor-default">
                <div className="h-56 relative overflow-hidden bg-gray-100">
                  {a.photo ? <img src={safeUrl(a.photo)} className="w-full h-full object-cover grayscale-[0.3] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-1000" alt="Activity" /> : <div className="w-full h-full flex items-center justify-center"><Trophy size={40} className="text-gray-300" /></div>}
                </div>
                <div className="p-10">
                  <h3 className="text-2xl font-[var(--font-heading)] italic text-[var(--color-primary)] mb-4">{a.title}</h3>
                  <p className="text-gray-500 font-light italic leading-relaxed">{a.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FACILITIES (ClassicIvy design, backbone data + View More) ── */}
      <section className="py-32 bg-[#f9f9f9]">
        <div className="container mx-auto px-8">
          <div className="flex flex-col md:flex-row justify-between items-end gap-12 mb-24 border-b border-gray-100 pb-12">
            <div className="max-w-xl">
              <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-gray-400 mb-6">Our Estate</div>
              <h2 className="text-5xl md:text-7xl font-medium text-[var(--color-primary)] italic" style={{ fontFamily: 'var(--font-heading)' }}>Campus Life.</h2>
            </div>
            <div className="flex gap-4">
              <button className="w-12 h-12 border border-gray-200 flex items-center justify-center hover:bg-[var(--color-primary)] hover:text-white transition-all" aria-label="Previous facility" title="Previous facility"><ChevronLeft size={18} /></button>
              <button className="w-12 h-12 border border-gray-200 flex items-center justify-center hover:bg-[var(--color-primary)] hover:text-white transition-all" aria-label="Next facility" title="Next facility"><ChevronRight size={18} /></button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            {data.facilities.slice(0, 4).map((fac: any, i: number) => (
              <div key={i} className="group grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div className="aspect-[4/5] overflow-hidden shadow-2xl relative">
                  {fac.photoFile ? <img src={safeUrl(fac.photoFile)} alt={fac.title} className="w-full h-full object-cover grayscale-[0.2] group-hover:grayscale-0 transition-all duration-1000" /> : <div className="w-full h-full bg-gray-100 flex items-center justify-center"><Building2 size={40} className="text-gray-300" /></div>}
                  <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md border border-white/30 px-3 py-1 text-[9px] font-bold uppercase tracking-widest text-white">{fac.categoryId}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold font-mono text-gray-400 uppercase tracking-widest mb-3">ESTATE // {String(i + 1).padStart(2, '0')}</div>
                  <h3 className="text-3xl font-[var(--font-heading)] text-[var(--color-primary)] italic mb-5">{fac.title}</h3>
                  <p className="text-gray-500 font-light italic leading-relaxed mb-6">{fac.description}</p>
                  <div className="w-10 h-[1px] bg-[var(--color-accent)]" />
                </div>
              </div>
            ))}
          </div>

          {data.facilities.length > 4 && (
            <div className="mt-20 text-center">
              <button
                onClick={() => setExpandedView('facilities')}
                className="border border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white px-12 h-12 text-[10px] font-bold uppercase tracking-[0.3em] transition-all inline-flex items-center gap-3"
              >
                View Full Estate Directory <ArrowUpRight size={14} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── PRINCIPAL (ClassicIvy design, backbone data) ── */}
      <section className="py-32 bg-[var(--color-primary)] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5"><Quote size={400} className="absolute -right-20 -bottom-20" /></div>
        <div className="container mx-auto px-8 relative z-10">
          <div className="grid lg:grid-cols-5 gap-24 items-center">
            <div className="lg:col-span-2">
              <div className="relative">
                <div className="absolute -inset-3 border-2 border-[var(--color-accent)]/30 translate-x-4 translate-y-4" />
                <div className="w-full aspect-[4/5] overflow-hidden shadow-2xl relative z-10">
                  {data.principal.photo ? <img src={safeUrl(data.principal.photo)} className="w-full h-full object-cover grayscale-[0.2]" alt="Principal" /> : <div className="w-full h-full bg-white/10 flex items-center justify-center"><Users size={60} className="text-white/30" /></div>}
                </div>
              </div>
            </div>
            <div className="lg:col-span-3 space-y-8">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-[var(--color-accent)] mb-6">Head of Institution</div>
                <h2 className="text-4xl md:text-6xl font-[var(--font-heading)] italic mb-6">Principal's Address.</h2>
                <div className="w-16 h-[2px] bg-[var(--color-accent)] mb-10" />
              </div>
              <p className="text-xl md:text-2xl font-light italic leading-relaxed opacity-90" style={{ fontFamily: 'var(--font-body)' }}>"{data.principal.message}"</p>
              <div>
                <p className="font-bold text-sm uppercase tracking-[0.3em]">{data.principal.name}</p>
                <p className="text-[10px] uppercase tracking-widest opacity-50 mt-1">Chief Principal</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STAFF & BOARD (ClassicIvy design, backbone data + View More) ── */}
      <section id="staff" className="py-32 bg-white">
        <div className="container mx-auto px-8">
          <div className="text-center mb-24">
            <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-[var(--color-primary)] mb-6">Distinguished Faculty & Governance</div>
            <h2 className="text-5xl md:text-7xl font-medium text-[var(--color-primary)] italic" style={{ fontFamily: 'var(--font-heading)' }}>Scholars & Board.</h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-24">
            {/* Faculty */}
            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-[0.5em] text-[var(--color-accent)] mb-12 border-b border-gray-100 pb-4">Academic Faculty</h4>
              <div className="grid grid-cols-2 gap-10">
                {data.staff.slice(0, 4).map((s: any, i: number) => (
                  <div key={i} className="group text-center">
                    <div className="relative mb-6 mx-auto w-40 h-48 overflow-hidden shadow-xl grayscale-[0.3] group-hover:grayscale-0 transition-all duration-700">
                      {s.photoFile ? <img src={safeUrl(s.photoFile)} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /> : <div className="w-full h-full bg-gray-100 flex items-center justify-center"><Users size={32} className="text-gray-300" /></div>}
                      <div className="absolute inset-0 border-4 border-white group-hover:border-2 transition-all duration-500" />
                    </div>
                    <h3 className="text-sm font-[var(--font-heading)] italic text-[var(--color-primary)] mb-1">{s.title} {s.name}</h3>
                    <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400">{s.bio || "Faculty"}</p>
                  </div>
                ))}
              </div>
              {data.staff.length > 4 && (
                <div className="mt-14">
                  <button
                    onClick={() => setExpandedView('staff')}
                    className="border border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white px-10 h-11 text-[10px] font-bold uppercase tracking-[0.3em] transition-all inline-flex items-center gap-3 w-full justify-center"
                  >
                    View Full Faculty Directory <ArrowUpRight size={13} />
                  </button>
                </div>
              )}
            </div>

            {/* Board */}
            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-[0.5em] text-[var(--color-accent)] mb-12 border-b border-gray-100 pb-4">Board of Management</h4>
              <div className="grid grid-cols-2 gap-10">
                {data.board.slice(0, 4).map((b: any, i: number) => (
                  <div key={i} className="group text-center">
                    <div className="relative mb-6 mx-auto w-40 h-48 overflow-hidden shadow-xl grayscale-[0.3] group-hover:grayscale-0 transition-all duration-700">
                      {b.photoFile ? <img src={safeUrl(b.photoFile)} alt={b.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /> : <div className="w-full h-full bg-gray-100 flex items-center justify-center"><School size={32} className="text-gray-300" /></div>}
                      <div className="absolute inset-0 border-4 border-white group-hover:border-2 transition-all duration-500" />
                    </div>
                    <h3 className="text-sm font-[var(--font-heading)] italic text-[var(--color-primary)] mb-1">{b.title} {b.name}</h3>
                    <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400">{b.role}</p>
                  </div>
                ))}
              </div>
              {data.board.length > 4 && (
                <div className="mt-14">
                  <button
                    onClick={() => setExpandedView('board')}
                    className="border border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white px-10 h-11 text-[10px] font-bold uppercase tracking-[0.3em] transition-all inline-flex items-center gap-3 w-full justify-center"
                  >
                    View Full Board Directory <ArrowUpRight size={13} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── GALLERY (ClassicIvy dark design, backbone data + View More) ── */}
      <section id="gallery" className="py-32 bg-[#1a1a1a] text-white">
        <div className="container mx-auto px-8">
          <div className="text-center mb-24">
            <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-[var(--color-accent)] mb-8">Visual Archives</div>
            <h2 className="text-5xl md:text-7xl font-[var(--font-heading)] italic mb-12 drop-shadow-lg">Captured Legacies.</h2>
            <div className="flex flex-wrap justify-center gap-10 mt-12">
              {['All', ...Array.from(new Set(data.gallery.map((g: any) => g.categoryId || g.category).filter(Boolean)))].map((cat: any) => (
                <button
                  key={cat}
                  onClick={() => setActiveTab(cat)}
                  className={`text-[10px] font-bold uppercase tracking-[0.3em] transition-all relative pb-2 ${activeTab === cat ? 'text-[var(--color-accent)]' : 'text-gray-500 hover:text-white'}`}
                >
                  {cat}
                  {activeTab === cat && <div className="absolute bottom-0 left-0 w-full h-[1px] bg-[var(--color-accent)]" />}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGallery.slice(0, 4).map((item: any, i: number) => (
              <div
                key={i}
                onClick={() => setLightboxIndex(i)}
                className="group relative aspect-square overflow-hidden border border-white/5 bg-black cursor-pointer"
              >
                {item.photoFile || item.photo ? <img src={safeUrl(item.photoFile || item.photo)} alt={item.name || "Gallery"} className="absolute inset-0 w-full h-full object-cover grayscale-[0.4] group-hover:grayscale-0 group-hover:scale-110 transition-all duration-[1500ms]" /> : <div className="absolute inset-0 w-full h-full flex items-center justify-center"><ImageIcon size={40} className="text-gray-700" /></div>}
                <div className="absolute inset-x-0 bottom-0 p-8 bg-gradient-to-t from-black via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                  <p className="text-[10px] font-bold text-[var(--color-accent)] uppercase tracking-widest mb-2">{item.categoryId || item.category}</p>
                  <h4 className="text-xl font-[var(--font-heading)] italic">{item.name}</h4>
                </div>
              </div>
            ))}
          </div>

          {filteredGallery.length > 4 && (
            <div className="mt-20 text-center">
              <button
                onClick={() => setExpandedView('gallery')}
                className="border border-[var(--color-accent)] text-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-[#1a1a1a] px-12 h-12 text-[10px] font-bold uppercase tracking-[0.3em] transition-all inline-flex items-center gap-3"
              >
                Browse Full Archive <ArrowUpRight size={14} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── ADMISSIONS & FEES (ClassicIvy design, backbone data) ── */}
      <section id="admissions" className="py-32 bg-white">
        <div className="container mx-auto px-8">
          <div className="text-center mb-24">
            <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-[var(--color-primary)] mb-6">Enrollment</div>
            <h2 className="text-5xl md:text-7xl font-medium text-[var(--color-primary)] italic" style={{ fontFamily: 'var(--font-heading)' }}>Admissions & Fees.</h2>
            <div className="w-16 h-[2px] bg-[var(--color-accent)] mx-auto mt-8" />
          </div>
          <div className="grid lg:grid-cols-2 gap-24">
            <div className="space-y-10">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[var(--color-primary)] mb-6">Enrollment Process</div>
                <p className="text-xl text-gray-500 font-light italic leading-relaxed border-l-4 border-[var(--color-accent)] pl-8">{data.admissions.process}</p>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-gray-400 mb-6">General Requirements</div>
                <ul className="space-y-4">
                  {data.admissions.requirements.map((req: string, i: number) => (
                    <li key={i} className="flex items-center gap-4 text-gray-600 font-light italic">
                      <div className="w-4 h-[1px] bg-[var(--color-accent)]" /> {req}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="space-y-8">
              <div className="text-[10px] font-bold uppercase tracking-[0.4em] text-[var(--color-primary)] mb-6">Fees & Finance</div>
              {data.payments.map((p: any, i: number) => (
                <div key={i} className="flex items-center justify-between p-8 border border-gray-100 hover:border-[var(--color-accent)] transition-all group">
                  <div>
                    <p className="text-[10px] font-bold text-[var(--color-accent)] uppercase tracking-widest mb-1 italic">{p.method}</p>
                    <p className="text-lg font-bold text-[var(--color-primary)]">{p.details}</p>
                  </div>
                  <ArrowUpRight size={18} className="text-gray-300 group-hover:text-[var(--color-accent)] transition-colors" />
                </div>
              ))}
              <div className="p-8 border-l-4 border-[var(--color-accent)] bg-gray-50">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3 italic">Policy Overview</p>
                <p className="text-sm font-light text-gray-500 italic">"{data.feePolicy}"</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── QUOTE & CORE VALUES (ClassicIvy design, backbone data) ── */}
      <section className="py-32 bg-[#f9f9f9] border-y border-gray-100">
        <div className="container mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-24 items-center">
            <div className="relative p-16 border border-gray-100 bg-white shadow-sm">
              <Quote size={60} className="text-[var(--color-primary)] opacity-10 mb-8 fill-current" />
              <p className="text-2xl md:text-3xl font-[var(--font-heading)] italic text-gray-600 leading-relaxed mb-10">"{data.quote.text}"</p>
              <div className="flex items-center gap-6">
                <div className="w-12 h-[1px] bg-[var(--color-accent)]" />
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--color-primary)]">{data.quote.author}</p>
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-gray-400 mb-6">Our Character</div>
              <h2 className="text-5xl md:text-6xl font-medium text-[var(--color-primary)] italic mb-12" style={{ fontFamily: 'var(--font-heading)' }}>Core Values.</h2>
              <div className="grid grid-cols-2 gap-4">
                {data.coreValues.map((v: string, i: number) => (
                  <div key={i} className="p-6 border border-gray-100 flex items-center gap-4 hover:border-[var(--color-accent)] transition-all bg-white cursor-default">
                    <div className="w-3 h-3 bg-[var(--color-accent)]" />
                    <span className="text-sm font-bold text-[var(--color-primary)] uppercase tracking-widest">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER (ClassicIvy grand estate design, backbone data) ── */}
      <footer id="contact" className="bg-[#1a1a1a] text-white/80 pt-32 pb-14 px-8 border-t-8 border-[var(--color-primary)]">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-20 mb-24">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-5 mb-10">
                <div className="w-12 h-12 bg-white/5 flex items-center justify-center overflow-hidden">
                  {data.logo ? <img src={safeUrl(data.logo)} alt="Logo" className="w-full h-full object-contain p-1" /> : <School size={28} className="text-[var(--color-accent)]" />}
                </div>
                <span className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>{data.name}</span>
              </div>
              <p className="text-base leading-relaxed mb-10 opacity-60 italic font-light">"{data.motto}"</p>
              <div className="space-y-4 mb-8">
                {data.contact.whatsapp && (
                  <a href={`https://wa.me/${data.contact.whatsapp}`} target="_blank" className="bg-green-800/30 border border-green-700/30 text-green-400 px-5 py-2.5 text-[10px] font-bold uppercase tracking-widest hover:bg-green-700/40 transition-all flex items-center gap-2 w-fit">
                    <MessageCircle size={14} /> WhatsApp
                  </a>
                )}
              </div>
              <div className="flex gap-4">
                {data.contact.facebook && <a href={data.contact.facebook} aria-label="Facebook" className="w-11 h-11 bg-white/5 border border-white/5 flex items-center justify-center hover:bg-[var(--color-accent)] hover:text-[#1a1a1a] transition-all"><Facebook size={18} /></a>}
                {data.contact.instagram && <a href={data.contact.instagram} aria-label="Instagram" className="w-11 h-11 bg-white/5 border border-white/5 flex items-center justify-center hover:bg-[var(--color-accent)] hover:text-[#1a1a1a] transition-all"><Instagram size={18} /></a>}
              </div>
            </div>

            <div>
              <h4 className="text-[10px] font-bold tracking-[0.5em] text-[var(--color-accent)] uppercase mb-10 border-b border-white/10 pb-4 inline-block">Directory</h4>
              <ul className="space-y-5 text-[10px] font-bold uppercase tracking-widest">
                {nav.map((item, i) => (
                  <li key={i}><a href={item.href} className="hover:text-[var(--color-accent)] transition-colors flex items-center gap-3 group"><ArrowRight size={11} className="opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" /> {item.label}</a></li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-[10px] font-bold tracking-[0.5em] text-[var(--color-secondary)] uppercase mb-10 border-b border-white/10 pb-4 inline-block">Contact</h4>
              <ul className="space-y-8">
                <li className="flex gap-5 group">
                  <MapPin size={22} className="text-[var(--color-accent)] opacity-40 group-hover:opacity-100 transition-opacity shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold tracking-widest uppercase opacity-40 mb-2 italic">Our Estate</p>
                    <p className="text-sm text-white leading-relaxed">{data.contact.address}</p>
                  </div>
                </li>
                {data.contact.phones.map((p: string, i: number) => (
                  <li key={i} className="flex gap-5 group">
                    <Phone size={22} className="text-[var(--color-accent)] opacity-40 group-hover:opacity-100 transition-opacity shrink-0" />
                    <div>
                      <p className="text-[10px] font-bold tracking-widest uppercase opacity-40 mb-2 italic">Registrar {i > 0 ? i + 1 : ''}</p>
                      <p className="text-sm text-white">{p}</p>
                    </div>
                  </li>
                ))}
                <li className="flex gap-5 group">
                  <Mail size={22} className="text-[var(--color-accent)] opacity-40 group-hover:opacity-100 transition-opacity shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold tracking-widest uppercase opacity-40 mb-2 italic">Email</p>
                    <p className="text-sm text-white">{data.contact.email}</p>
                  </div>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-[10px] font-bold tracking-[0.5em] text-white uppercase mb-10 border-b border-white/10 pb-4 inline-block">Location</h4>
              <div className="h-44 bg-white/5 border border-white/10 overflow-hidden relative">
                {data.contact.mapsLink ? (
                  <iframe title="School map" src={data.contact.mapsLink} className="w-full h-full border-none grayscale pointer-events-none" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/20 text-xs italic">Map not configured</div>
                )}
                <a href={data.contact.mapsLink} aria-label="Open map" className="absolute bottom-2 right-2 bg-[var(--color-accent)] text-[#1a1a1a] p-2"><ArrowUpRight size={13} /></a>
              </div>
            </div>
          </div>

          <div className="pt-16 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-8 text-[10px] font-bold tracking-[0.5em] uppercase opacity-30">
            <p>&copy; {new Date().getFullYear()} {data.name} // ALL RIGHTS RESERVED</p>
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="border border-white/20 px-6 py-2 hover:text-white hover:border-white transition-all">Back to Top ↑</button>
          </div>
        </div>
      </footer>

      {/* ── MOBILE MENU (ClassicIvy design) ── */}
      {menuOpen && (
        <div className="fixed inset-0 z-[100] bg-[#1a1a1a] p-12 flex flex-col justify-between text-white">
          <div className="flex justify-between items-center">
            <div className="text-xl italic font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{data.name}</div>
            <X size={36} className="cursor-pointer" onClick={() => setMenuOpen(false)} />
          </div>
          <nav className="flex flex-col gap-8">
            {nav.map((n, i) => (
              <a key={i} href={n.href} onClick={() => setMenuOpen(false)} className="text-4xl font-[var(--font-heading)] italic hover:text-[var(--color-accent)] transition-all">
                {n.label}
              </a>
            ))}
          </nav>
          <div className="border-t border-white/5 pt-10">
            <a href="#contact" onClick={() => setMenuOpen(false)} className="w-full bg-[var(--color-accent)] text-[#1a1a1a] h-14 font-bold uppercase tracking-[0.3em] text-[10px] inline-flex items-center justify-center hover:opacity-90 transition-all">
              Contact Us
            </a>
          </div>
        </div>
      )}

      {/* ── EXPANDED DIRECTORY OVERLAY (backbone logic, ClassicIvy style) ── */}
      {expandedView && (
        <div className="fixed inset-0 z-[200] bg-[var(--color-background)] overflow-y-auto pb-24">
          <div className="bg-white border-b border-gray-200 px-8 py-6 flex items-center justify-between sticky top-0 z-50 shadow-sm">
            <div className="flex items-center gap-5">
              <button onClick={() => setExpandedView(null)} className="w-10 h-10 border border-gray-200 flex items-center justify-center hover:bg-[var(--color-primary)] hover:text-white hover:border-[var(--color-primary)] transition-all" aria-label="Go back" title="Go back">
                <ChevronLeft size={20} />
              </button>
              <div>
                <h2 className="text-xl font-[var(--font-heading)] italic text-[var(--color-primary)]">
                  {expandedView === 'staff' ? 'Academic Faculty Directory' : expandedView === 'gallery' ? 'Visual Archive' : expandedView === 'facilities' ? 'Campus Facilities Directory' : expandedView === 'anthems' ? 'Institutional Anthems' : 'Board of Management Directory'}
                </h2>
                <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400 mt-0.5">{data.name}</p>
              </div>
            </div>
            <button onClick={() => setExpandedView(null)} className="text-gray-400 hover:text-[var(--color-primary)] p-2" aria-label="Close overlay" title="Close overlay"><X size={22} /></button>
          </div>

          <div className="max-w-7xl mx-auto px-8 py-16">
            {expandedView === 'gallery' ? (
              <>
                <div className="flex flex-wrap gap-8 justify-center mb-16">
                  {['All', ...Array.from(new Set(data.gallery.map((g: any) => g.categoryId || g.category).filter(Boolean)))].map((cat: any) => (
                    <button
                      key={cat}
                      onClick={() => setActiveTab(cat)}
                      className={`text-[10px] font-bold uppercase tracking-[0.3em] transition-all relative pb-2 ${activeTab === cat ? 'text-[var(--color-primary)]' : 'text-gray-400 hover:text-[var(--color-primary)]'}`}
                    >
                      {cat}
                      {activeTab === cat && <div className="absolute bottom-0 left-0 w-full h-[1px] bg-[var(--color-accent)]" />}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredGallery.map((item: any, i: number) => (
                    <div key={i} onClick={() => setLightboxIndex(i)} className="group relative aspect-square overflow-hidden border border-gray-100 bg-gray-50 cursor-pointer">
                      {item.photoFile || item.photo ? <img src={safeUrl(item.photoFile || item.photo)} alt={item.name || "Gallery"} className="absolute inset-0 w-full h-full object-cover grayscale-[0.3] group-hover:grayscale-0 group-hover:scale-110 transition-all duration-[1500ms]" /> : <div className="absolute inset-0 flex items-center justify-center"><ImageIcon size={40} className="text-gray-300" /></div>}
                      <div className="absolute inset-x-0 bottom-0 p-8 bg-gradient-to-t from-black via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
                        <p className="text-[10px] font-bold text-[var(--color-accent)] uppercase tracking-widest mb-2">{item.categoryId || item.category}</p>
                        <h4 className="text-xl font-[var(--font-heading)] italic text-white">{item.name}</h4>
                        {item.description && <p className="text-white/60 text-xs mt-1 line-clamp-2 italic">{item.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : expandedView === 'facilities' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                {data.facilities.map((fac: any, i: number) => (
                  <div key={i} className="group grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                    <div className="aspect-[4/5] overflow-hidden shadow-xl relative">
                      {fac.photoFile ? <img src={safeUrl(fac.photoFile)} alt={fac.title} className="w-full h-full object-cover grayscale-[0.2] group-hover:grayscale-0 transition-all duration-700" /> : <div className="w-full h-full bg-gray-100 flex items-center justify-center"><Building2 size={40} className="text-gray-300" /></div>}
                      <div className="absolute top-4 right-4 bg-white/20 backdrop-blur px-3 py-1 text-[9px] font-bold uppercase tracking-widest text-white">{fac.categoryId}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono text-gray-400 uppercase tracking-widest mb-3">ESTATE // {String(i + 1).padStart(2, '0')}</div>
                      <h3 className="text-2xl font-[var(--font-heading)] italic text-[var(--color-primary)] mb-4">{fac.title}</h3>
                      <p className="text-gray-500 font-light italic leading-relaxed">{fac.description}</p>
                      <div className="w-10 h-[1px] bg-[var(--color-accent)] mt-6" />
                    </div>
                  </div>
                ))}
              </div>
            ) : expandedView === 'anthems' ? (
              <div className="max-w-4xl mx-auto space-y-24">
                {/* National Anthem */}
                <div className="space-y-12">
                  <div className="text-center space-y-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-[var(--color-accent)]">The Republic of Kenya</div>
                    <h3 className="text-4xl md:text-5xl font-[var(--font-heading)] italic text-[var(--color-primary)]">National Anthem</h3>
                    <div className="w-16 h-[2px] bg-[var(--color-accent)] mx-auto" />
                  </div>
                  <div className="aspect-video w-full overflow-hidden shadow-2xl relative">
                    <img src="https://images.unsplash.com/photo-1608925565154-2974eb36e6ba?q=80&w=2070" className="w-full h-full object-cover grayscale-[0.3]" alt="National Anthem" />
                    <div className="absolute inset-0 bg-[var(--color-primary)]/40" />
                  </div>
                  <div className="bg-white border border-gray-100 shadow-sm p-12 md:p-16 text-center">
                    <p className="whitespace-pre-line text-xl text-gray-600 leading-loose italic font-light" style={{ fontFamily: 'var(--font-body)' }}>{data.nationalAnthem}</p>
                  </div>
                </div>
                {/* School Anthem */}
                <div className="space-y-12 pt-16 border-t-2 border-[var(--color-accent)]/30">
                  <div className="text-center space-y-4">
                    <div className="text-[10px] font-bold uppercase tracking-[0.5em] text-[var(--color-accent)]">{data.name}</div>
                    <h3 className="text-4xl md:text-5xl font-[var(--font-heading)] italic text-[var(--color-primary)]">School Anthem</h3>
                    <div className="w-16 h-[2px] bg-[var(--color-accent)] mx-auto" />
                  </div>
                  <div className="aspect-video w-full overflow-hidden shadow-2xl relative">
                    <img src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2070" className="w-full h-full object-cover grayscale-[0.3]" alt="School Anthem" />
                    <div className="absolute inset-0 bg-[var(--color-primary)]/40" />
                  </div>
                  <div className="bg-white border border-gray-100 shadow-sm p-12 md:p-16 text-center">
                    <p className="whitespace-pre-line text-xl text-gray-600 leading-loose italic font-light" style={{ fontFamily: 'var(--font-body)' }}>{data.schoolAnthem}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
                {(expandedView === 'staff' ? data.staff : data.board).map((item: any, i: number) => (
                  <div key={i} className="group text-center">
                    <div className="relative mb-8 mx-auto w-44 h-52 overflow-hidden shadow-xl grayscale-[0.2] group-hover:grayscale-0 transition-all duration-700">
                      {(item.photoFile || item.photo) ? (
                        <img src={safeUrl(item.photoFile || item.photo)} alt={item.name || "Profile"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      ) : (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          {expandedView === 'staff' ? <Users size={48} className="text-gray-300" /> : <School size={48} className="text-gray-300" />}
                        </div>
                      )}
                      <div className="absolute inset-0 border-4 border-white group-hover:border-2 transition-all duration-500" />
                    </div>
                    <h3 className="font-[var(--font-heading)] italic text-[var(--color-primary)] text-lg mb-1">{item.title} {item.name}</h3>
                    <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400">{expandedView === 'staff' ? (item.bio || 'Faculty') : item.role}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── LIGHTBOX (backbone logic, ClassicIvy style) ── */}
      {lightboxIndex !== null && filteredGallery[lightboxIndex] && (
        <div className="fixed inset-0 bg-black flex items-center justify-center" style={{ zIndex: 99999 }}>
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-8 left-8 z-50 text-white/70 hover:text-white transition-all flex items-center gap-2 font-bold uppercase tracking-[0.3em] text-[10px] bg-black/40 px-6 py-3 border border-white/10"
          >
            <ChevronLeft size={14} /> Back to Gallery
          </button>
          <div className="absolute inset-0 w-full h-full">
            {(filteredGallery[lightboxIndex] as any).photoFile || (filteredGallery[lightboxIndex] as any).photo ? (
              <img src={safeUrl((filteredGallery[lightboxIndex] as any).photoFile || (filteredGallery[lightboxIndex] as any).photo)} alt={(filteredGallery[lightboxIndex] as any).name || "Gallery"} className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full bg-[#1a1a1a] flex items-center justify-center text-white/10"><ImageIcon size={80} /></div>
            )}
          </div>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-40 bg-black/80 border border-white/10 px-10 py-6 max-w-2xl w-[90%] text-center backdrop-blur-sm">
            <p className="text-[10px] font-bold text-[var(--color-accent)] uppercase tracking-[0.3em] mb-2">{(filteredGallery[lightboxIndex] as any).categoryId || (filteredGallery[lightboxIndex] as any).category}</p>
            <h3 className="text-white font-[var(--font-heading)] italic text-2xl leading-tight mb-1">{(filteredGallery[lightboxIndex] as any).name || "Untitled"}</h3>
            <p className="text-white/50 text-xs italic hidden md:block">{(filteredGallery[lightboxIndex] as any).description || ""}</p>
          </div>
          <div className="absolute inset-0 pointer-events-none flex items-center justify-between px-8 z-20">
            {lightboxIndex > 0 ? (
              <button onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }} className="pointer-events-auto text-white/60 hover:text-white bg-black/40 hover:bg-black/60 px-6 py-4 border border-white/10 font-bold uppercase text-[10px] tracking-widest">
                Prev
              </button>
            ) : <div />}
            {lightboxIndex < filteredGallery.length - 1 ? (
              <button onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }} className="pointer-events-auto text-white/60 hover:text-white bg-black/40 hover:bg-black/60 px-6 py-4 border border-white/10 font-bold uppercase text-[10px] tracking-widest">
                Next
              </button>
            ) : <div />}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassicIvy;
